<template>
	<div>
		<div v-if="topBarItems?.length > 0" class="text-white bg-blue-darker">
			<VerticalSlider
				:items="topBarItems"
				class="container mx-auto flex justify-center items-center h-[42px] text-[15px]"
			/>
		</div>

		<div class="relative lg:px-0 px-2.5 bg-blue">
			<div class="container mx-auto">
				<div class="flex justify-between items-center py-[11px] space-x-4">
					<div class="flex-[2_2_0%] flex gap-4">
						<LayoutSideMenu
							:menu-categories-norce="menuCategoriesNorce"
							:menu-categories="menuCategories"
							:menu="menu"
							:top-navbar-left="topNavbarLeft"
							:cultures-norce="culturesNorce"
							:currencies-norce="currenciesNorce"
						/>
					</div>
					<div class="flex-[2_2_0%] flex justify-center">
						<div class="flex justify-center items-center">
							<NuxtLink :to="localePath(`/`)">
								<NuxtImg
									:src="siteLogo?.filename"
									:alt="siteLogo?.alt || 'logo'"
									class="logo"
									loading="eager"
									:width="siteLogoDimensions?.width"
									:height="siteLogoDimensions?.height"
								/>
							</NuxtLink>
						</div>
					</div>

					<div class="flex-[2_2_0%] flex gap-4 items-center justify-end">
						<button type="button" @click="isSearchOpen = !isSearchOpen">
							<MagnifyingGlassIcon class="h-6 w-6 text-white" />
							<span class="sr-only">{{ $t("Open search") }}</span>
						</button>
						<div class="flow-root">
							<button
								class="group -m-2 p-2 flex items-center relative"
								aria-label="wishlist"
								data-testid="wishlist-button"
								@click="openWishlist"
							>
								<HeartIcon
									class="w-6 h-6 text-white hover:text-brand-primary"
								/>
								<client-only>
									<span
										v-if="wishlistProducts?.ItemCount"
										class="text-[10px] font-medium text-white absolute bg-green-light rounded-full w-5 h-5 top-0 right-0 leading-5"
									>
										{{ wishlistProducts?.ItemCount }}
									</span>
								</client-only>
							</button>
						</div>
						<!-- Cart -->
						<div
							class="relative cursor-pointer"
							data-testid="cart-toggle"
							@click="isSidebarOpen = true"
						>
							<ShoppingBagIcon class="h-6 w-6 text-white cursor-pointer" />
							<client-only>
								<span
									data-testid="cart-badge"
									class="text-[10px] font-medium text-white text-center absolute bg-green-light rounded-full w-5 h-5 leading-5 -top-2 -right-2"
								>
									{{ itemsCount || 0 }}
								</span>
							</client-only>
						</div>
					</div>
				</div>
				<div
					v-show="isSearchOpen"
					class="absolute bottom-[-56px] left-0 right-0 py-2.5 px-[5px] bg-blue"
				>
					<StoryblokComponent
						v-for="blok in search"
						:key="blok._uid"
						:blok="blok"
					/>
				</div>
				<LayoutSideCart />
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	HeartIcon,
	MagnifyingGlassIcon,
	ShoppingBagIcon
} from "@heroicons/vue/24/outline";
import { provide, ref } from "vue";
import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { Currency } from "@nwt/norce-pwa/dist/runtime/types/api/application/application";
import type {
	CulturesNorce,
	SiteLogoDimensions
} from "~/types/components/header";
import type { SharedModal } from "~/layers/base/components/Shared/SharedModal.vue";
import type {
	MenuLink,
	ResolvedMenuNode,
	Search,
	SiteLogo,
	TopNavbarLeft,
	TopBarItem
} from "~/types/storyblok/storyblok";
import { storeToRefs } from "pinia";

const cartStore = useCartStore();
const localePath = useLocalePath();

const itemsCount = computed(() => cartStore.itemsCount());

const isSidebarOpen = ref(false);

provide("isSidebarOpen", isSidebarOpen);

interface Props {
	topNavbarLeft: TopNavbarLeft[] | undefined;
	culturesNorce: CulturesNorce[] | undefined;
	currenciesNorce: Currency[] | undefined;
	search: Search[] | undefined;
	siteLogo: SiteLogo | undefined;
	siteLogoDimensions: SiteLogoDimensions;
	menuCategoriesNorce: Category[] | undefined;
	menuCategories: MenuLink[] | undefined;
	menu?: ResolvedMenuNode[];
	topBarItems: TopBarItem[] | undefined;
}

const {
	topNavbarLeft,
	culturesNorce,
	currenciesNorce,
	search,
	siteLogo,
	siteLogoDimensions,
	menuCategoriesNorce,
	menuCategories,
	menu,
	topBarItems
} = defineProps<Props>();

const { wishlistProducts } = storeToRefs(useWishlistStore());
const isSearchOpen = ref(false);

const openWishlist = () => {
	modal.open("AccountWishlist", {
		position: "side"
	});
};

const modal = inject<SharedModal>("modal") as SharedModal;
</script>
